import { Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import { CustomerShell } from '@/shells/CustomerShell';
import { StaffShell } from '@/shells/StaffShell';
import { OperationsShell } from '@/shells/OperationsShell';
import { ManageShell } from '@/shells/ManageShell';
import { BillPage } from '@/features/customer/BillPage';
import { CartPage } from '@/features/customer/CartPage';
import { CustomerLayout, CustomerNotFound } from '@/features/customer/CustomerLayout';
import { EntryPage } from '@/features/customer/EntryPage';
import { HelpPage } from '@/features/customer/HelpPage';
import { LandingPage } from '@/features/customer/LandingPage';
import { KitchenPage } from '@/features/kitchen/KitchenPage';
import { FloorPage } from '@/features/staff/FloorPage';
import { MenuPage as StaffMenuPage } from '@/features/staff/MenuPage';
import { ReadyPage } from '@/features/staff/ReadyPage';
import { RequestsPage } from '@/features/staff/RequestsPage';
import { SessionPage } from '@/features/staff/SessionPage';
import { StaffGate, StaffNotFound } from '@/features/staff/StaffGate';
import { CounterPage } from '@/features/counter/CounterPage';
import { AdminGate } from '@/features/admin/AdminGate';
import { AdminPage } from '@/features/admin/AdminPage';
import { LocationsPage } from '@/features/manage/LocationsPage';
import { ManageGate, ManageNotFound } from '@/features/manage/ManageGate';
import { MenuPage as ManageMenuPage } from '@/features/manage/MenuPage';
import { OverviewPage } from '@/features/manage/OverviewPage';
import { MenuPage } from '@/features/customer/MenuPage';
import { OrderDetailPage, OrdersPage } from '@/features/customer/OrdersPage';
import { ReviewPage } from '@/features/customer/ReviewPage';
import { RequireStaffSession } from './guards';
import { Login } from './Login';
import { NotFound, ShellNotFound } from './pages';

const ops = (title: string) => <OperationsShell title={title} />;

/** One route table, used by the browser router in the app and by memory routers in tests. */
export const appRoutes: RouteObject[] = [
  // The bare address has no venue behind it, so it has its own full-width page rather than the guest shell.
  { path: '/', element: <LandingPage /> },
  {
    element: <CustomerShell showNav={false} />,
    children: [{ path: '/q/:qrToken', element: <EntryPage /> }],
  },
  {
    path: '/t',
    element: <CustomerLayout />,
    children: [
      { index: true, element: <Navigate to="menu" replace /> },
      { path: 'menu', element: <MenuPage /> },
      { path: 'cart', element: <CartPage /> },
      { path: 'cart/review', element: <ReviewPage /> },
      { path: 'orders', element: <OrdersPage /> },
      { path: 'orders/:orderId', element: <OrderDetailPage /> },
      { path: 'bill', element: <BillPage /> },
      { path: 'help', element: <HelpPage /> },
      { path: '*', element: <CustomerNotFound /> },
    ],
  },
  { path: '/login', element: <Login /> },
  {
    element: <RequireStaffSession />,
    children: [
      {
        // The gate sits outside the shell: a refused role sees "Not allowed" without the waiter tabs.
        path: '/staff',
        element: <StaffGate />,
        children: [
          {
            element: <StaffShell />,
            children: [
              { index: true, element: <FloorPage /> },
              { path: 'sessions/:sessionId', element: <SessionPage /> },
              { path: 'ready', element: <ReadyPage /> },
              { path: 'requests', element: <RequestsPage /> },
              { path: 'menu', element: <StaffMenuPage /> },
              { path: '*', element: <StaffNotFound /> },
            ],
          },
        ],
      },
      {
        path: '/kitchen',
        element: ops('Kitchen'),
        children: [
          { index: true, element: <KitchenPage /> },
          { path: '*', element: <ShellNotFound to="/kitchen" label="Back to the kitchen" /> },
        ],
      },
      {
        path: '/counter',
        element: ops('Counter'),
        children: [
          { index: true, element: <CounterPage /> },
          { path: '*', element: <ShellNotFound to="/counter" label="Back to the counter" /> },
        ],
      },
      {
        // The gate sits outside the shell: a refused role sees "Not allowed" without the management navigation.
        path: '/manage',
        element: <ManageGate />,
        children: [
          {
            element: <ManageShell />,
            children: [
              { index: true, element: <OverviewPage /> },
              { path: 'locations', element: <LocationsPage /> },
              { path: 'menu', element: <ManageMenuPage /> },
              { path: '*', element: <ManageNotFound /> },
            ],
          },
        ],
      },
      {
        // The gate sits outside the shell: a refused role sees "Not allowed" under the plain header only.
        path: '/admin',
        element: <AdminGate />,
        children: [
          {
            element: ops('Admin'),
            children: [
              { index: true, element: <AdminPage /> },
              {
                path: '*',
                element: <ShellNotFound to="/admin" label="Back to administration" />,
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
];
